import { useRequest } from 'ahooks'
import { Button } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getPointPermission } from '@/components'
import { JQLTable, JiraResp } from '@/components/jql/jql-table.component'
import { PAGE_RESOURCE_POINTS_ENUM, Project, ResourceType } from '@/schemas'

import { ProjectJQLEdit } from './project-jql-edit.component'
import { JiraColumnProps, ProjectJqlTableConfig } from './project-jql-table-config.component'

interface ProjectJQLProps {
  project: Project
}

export const ProjectJQLContainer: React.FC<ProjectJQLProps> = ({ project }) => {
  const { t } = useTranslation()
  const { selfId, jql: initialJql } = project
  const { editable: editableConfig, canRead } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.需求与问题_配置,
    project.permissions
  )
  const { editable: editableContent } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.需求与问题_内容,
    project.permissions
  )
  const [editVisible, setEditVisible] = React.useState(false)
  const [configVisible, setConfigVisible] = React.useState(false)
  const [jql, setJql] = React.useState(initialJql)

  const {
    data: jiraTableData,
    run: jiraTableRefresh,
    loading: jiraTableLoading,
  } = useRequest<JiraColumnProps[]>(() => ({
    url: '/api/project/getJiraTableConfig',
    method: 'get',
    params: {
      selfId,
      type: ResourceType.PROJECT,
    },
  }))

  const { data, loading, run } = useRequest<JiraResp>(
    {
      url: '/api/project/getJiraAndUpdate',
      params: {
        selfId: project.selfId,
        type: ResourceType.PROJECT,
        jql,
      },
    },
    {
      manual: true,
      refreshOnWindowFocus: false,
      debounceInterval: 800,
    }
  )

  // ready和refreshDeps配合时只会在第一次生效
  // https://github.com/alibaba/hooks/issues/468
  React.useEffect(() => {
    if (jql) {
      run()
    }
  }, [jql])

  const issues = data?.issues || []
  const hasBugField = issues.some((issue) => issue.bugResolved !== 'N/A')
  const hasReleaseStatusField = issues.some((issue) => issue.isReleaseType)

  const dropdown = (
    <div className='flex mb-4 justify-between'>
      <Button type='primary' disabled={!canRead} onClick={() => setConfigVisible(true)}>
        {t('list config')}
      </Button>
      <Button disabled={!editableContent} type='primary' onClick={() => setEditVisible(true)}>
        {t('associate jira')}
      </Button>
    </div>
  )

  const table = jql && (
    <JQLTable
      columns={jiraTableData || []}
      issues={issues}
      loading={loading || jiraTableLoading}
      entity={{ name: project.name, type: ResourceType.PROJECT }}
    />
  )

  return (
    <div>
      {dropdown}
      {table}
      <ProjectJQLEdit
        columns={jiraTableData || []}
        selfId={selfId}
        jql={jql}
        onSuccess={setJql}
        visible={editVisible}
        setVisible={setEditVisible}
        project={project}
      />
      <ProjectJqlTableConfig
        editable={editableConfig}
        columns={jiraTableData || []}
        selfId={selfId}
        visible={configVisible}
        setVisible={setConfigVisible}
        refresh={jiraTableRefresh}
        hasBugField={hasBugField}
        hasReleaseStatusField={hasReleaseStatusField}
      />
    </div>
  )
}
